/**
 * ScreenerPanel — the detail grid plus the server-computed rollups, behind tabs.
 *
 * The rollups are separate *frames*, not grid row groups: AG Grid Community has
 * no `rowGroup`/`aggFunc`, so qv-data aggregates by sector and industry and
 * sends each as its own frame. Each tab is therefore an ordinary flat grid, and
 * the Enterprise upgrade later becomes additive rather than a rewrite.
 *
 * Only the active tab's grid is mounted. Keeping three grids alive would triple
 * the row rendering for two views nobody is looking at.
 *
 * The active tab is ephemeral view state, not contract data (D8) — it lives in
 * component state and is deliberately never persisted to the workspace.
 */

import { useMemo, useState } from "react";
import type {
  SavedScreenerFilter,
  ScreenerConfig,
} from "@qufolio/model";
import type { ScreenerColumnMeta, ScreenerFrame } from "@qufolio/transport";
import type { CategoryOf } from "../lib/bands.js";


import {
  ScreenerGrid,
  type ScreenerConfigPatch,
  type SymbolHoverBox,
  type TrackListBinding,
  type ScreenerExporter,
} from "./ScreenerGrid.js";
import { dataFramesOf } from "../lib/data-frames.js";
import type { AddRowBinding } from "../lib/add-row.js";
import { ColumnPicker } from "./ColumnPicker.js";
import { ScreenerBuilder, type BuilderTab } from "./ScreenerBuilder.js";
import { buildUniverse } from "../lib/column-universe.js";
import { readColumnSources } from "../lib/column-source.js";
import type { TokenResolver } from "../lib/columns.js";
import { themeCssVars } from "../lib/theme.js";
import type { NewsOpen } from "./NewsCell.js";

export interface ScreenerPanelProps {
  frames: readonly ScreenerFrame[];
  /** Per-column metadata from qv-data: labels, friendly names, timeframes. */
  meta?: readonly ScreenerColumnMeta[];
  config: ScreenerConfig;
  /**
   * Resolves semantic color tokens (D13). Drives the panel chrome as well as
   * cell formatting: the chrome's style constants are module-level and cannot
   * call a hook, so the resolved tokens are published as CSS variables on this
   * root and inherited by everything below.
   */
  resolveToken?: TokenResolver;
  /** BCP 47 for the screened market — see `ScreenerGrid`. */
  locale?: string;
  /** AG Grid theme class, chosen by the active theme's mode (`agThemeClass`). */
  themeClass?: string;
  onConfigChange?: (patch: ScreenerConfigPatch) => void;
  /**
   * The saved-filter library changed. Separate from `onConfigChange` because a
   * library is not section state — editing a filter affects every section using
   * it, which is what a library means.
   */
  onSavedFiltersChange?: (filters: SavedScreenerFilter[]) => void;
  /**
   * Whether this caller may arrange the library — ★ a condition, move a group.
   * False on a screen somebody else owns (a group's, seen by a member), where a
   * change would go nowhere. Defaults to true, as every host had before.
   */
  canEditLibrary?: boolean;
  /** Rows showing after filtering, and rows received. Detail tab only. */
  onRowCounts?: (matched: number, total: number) => void;
  /** The detail grid's rows for Export — `ScreenerGrid`'s `onExporter`. */
  onExporter?: (exporter: ScreenerExporter | null) => void;
  /** A symbol was picked in the detail grid — drive the chart from it. */
  onSelectSymbol?: (symbol: string) => void;
  /** Passed through to the grid — see `ScreenerGridProps.onHoverSymbol`. */
  onHoverSymbol?: (symbol: string | null, at: SymbolHoverBox | null) => void;
  /** Every selected symbol in the detail grid — the bulk track-list path. */
  onSelectedSymbols?: (symbols: readonly string[]) => void;
  /**
   * Track lists: the star column, detail tab only.
   *
   * Withheld from the rollups deliberately. A rollup row's key is a sector or an
   * industry, and a sector is not something you put on a watchlist — the column
   * would render a star that could only ever refuse.
   */
  track?: TrackListBinding;
  /** A Movers News cell was clicked — detail tab only, a symbol's headlines. */
  onNewsOpen?: NewsOpen;
  /**
   * The Add row, detail tab only — for `track`'s reason: a rollup row is a
   * sector, and nothing typed into one could be filed anywhere.
   */
  addRow?: AddRowBinding;
  /**
   * What the Symbol column's search is about — see `ScreenerGrid`. A change
   * closes and empties it.
   */
  searchScope?: string;
  /** The market is live now — see `ScreenerGrid`. */
  /** See `ScreenerGrid`'s: size to the rows rather than fill. */
  autoHeight?: boolean;
  liveWindow?: boolean;
  /** An indicator's catalog category — see `ScreenerGrid`. */
  categoryOf?: CategoryOf;
  /** Forwarded to the detail grid's News row action. */
  /** Mints a ULID for saved filters. Injected so this package needs no id dep. */
  mintId?: () => string;
  /** Status line rendered beside the tabs (connection state, row counts…). */
  status?: React.ReactNode;
  /**
   * Re-read the screen from the server, recomputing rather than serving a
   * cached frame. Omitted ⇒ no button, which is what a host with no live
   * server should show.
   *
   * A callback and not a URL: this island must stay WebView-hostable (D18), so
   * it knows that a refresh was *asked for* and nothing about how one is done.
   */
  onRefresh?: () => void;
  /** A refresh is in flight — the button says so and cannot be pressed twice. */
  refreshing?: boolean;
  /**
   * Suppress the Columns button, for a host that offers one of its own.
   *
   * Additive rather than a removal, because the two callers genuinely differ:
   * the web app has moved Columns into its group bar and would otherwise show
   * two of them a few centimetres apart, while a host embedding this island on
   * its own — a WebView on desktop or mobile, per D18 — has no other chrome and
   * needs the button exactly where it is. `onConfigChange` is still required
   * either way: the grid writes column resizes and moves back through it.
   */
  hideColumns?: boolean;
}

const TAB_LABELS: Record<string, string> = {
  detail: "Symbols",
  sector: "Sectors",
  industry: "Industries",
};

/**
 * The column view for a rollup frame, derived from the detail view.
 *
 * A rollup's schema is not the detail's: it has the group key and `count` in
 * place of `symbol`/`name`, and its numeric columns are aggregates. Handing it
 * the detail view unchanged hides `count` — the most useful column on the tab —
 * behind the column menu, since anything the view doesn't name is hidden.
 *
 * So: group key and count first, then whichever of the detail's configured
 * columns actually survived aggregation, keeping their formatting.
 */
function rollupConfig(config: ScreenerConfig, frame: ScreenerFrame): ScreenerConfig {
  const present = new Set(frame.fields.map((f) => f.name));
  const key = frame.name;

  const inherited = (config.columns ?? []).filter(
    (c) => c.field !== key && c.field !== "count" && present.has(c.field),
  );

  return {
    ...config,
    columns: [
      { field: key, header: key[0]!.toUpperCase() + key.slice(1), pinned: "left", width: 160 },
      { field: "count", header: "Symbols", precision: 0, width: 95 },
      ...inherited,
    ],
    // Sort and filter are written against the detail schema; a rollup shares
    // almost none of it, so applying them would silently filter everything out.
    filter: undefined,
    sort: [{ field: "count", dir: "desc" }],
  };
}


export function ScreenerPanel({
  frames,
  meta,
  config,
  resolveToken,
  locale,
  themeClass,
  onConfigChange,
  onSavedFiltersChange,
  canEditLibrary = true,
  mintId,
  status,
  onRefresh,
  refreshing,
  hideColumns,
  onRowCounts,
  onExporter,
  onSelectSymbol,
  onHoverSymbol,
  onSelectedSymbols,
  track,
  onNewsOpen,
  addRow,
  searchScope,
  liveWindow,
  autoHeight,
  categoryOf,
}: ScreenerPanelProps) {
  const [active, setActive] = useState("detail");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [builderTab, setBuilderTab] = useState<BuilderTab | null>(null);
  // The column-funnel layer. Transient by design — see ScreenerGrid's
  // handleFilterChanged for why persisting it destroys saved filters.

  // The `columns` frame describes the other frames rather than being one — it is
  // per-column where they are per-row. Rendering it as a tab would offer the
  // user a grid of metadata, and applying a data frame's column view to it hides
  // every column, so it must be excluded from the tab strip explicitly.
  const dataFrames = useMemo(() => dataFramesOf(frames), [frames]);

  // What the server acknowledged serving, per column. Derived from the frames
  // rather than taken as a prop: the `columns` frame travels with them, so any
  // host that can render this panel already has everything this needs — and a
  // prop would let a host pass an acknowledgement that disagrees with the
  // payload it came in.
  const sources = useMemo(() => readColumnSources(frames), [frames]);

  // Frames are selected by name, never by index, so a data frame added by a
  // newer server appears as a tab here without any change to this component.
  const current = dataFrames.find((f) => f.name === active) ?? dataFrames[0];

  const isDetail = current?.name === "detail";
  const currentConfig = useMemo(
    () => (!current || isDetail ? config : rollupConfig(config, current)),
    [config, current, isDetail],
  );

  // Built from the frame on screen, so the picker offers exactly what that tab
  // can show — a rollup has a different schema from the detail frame.
  const universe = useMemo(
    () =>
      buildUniverse({
        fields: current?.fields ?? [],
        meta,
        columns: currentConfig.columns,
        sources,
      }),
    [current?.fields, meta, currentConfig.columns, sources],
  );

  const stripHasContent =
    dataFrames.length > 1 ||
    Boolean(onRefresh) ||
    (status !== undefined && status !== null && status !== false && status !== "") ||
    Boolean(isDetail && onConfigChange && !hideColumns);

  return (
    <div
      style={{
        // Resolved tokens as CSS variables: every style constant below (and in
        // the picker, filter bar and builder) reads `var(--qf-*)` and inherits
        // from here, so a theme switch repaints the island without a single
        // component knowing a theme exists.
        ...themeCssVars(resolveToken),
        position: "relative", // positioning context for the column picker popover
        display: "flex",
        flexDirection: "column",
        height: autoHeight ? "auto" : "100%",
        minHeight: 0,
        background: "var(--qf-surface)",
        color: "var(--qf-text)",
      }}
    >
      {/* **Only when something is in it.** A host that owns Refresh and
          Columns itself (the web's group bar) and is connected leaves this row
          with no tab, no button and an empty status — an empty strip with a
          border over the grid (owner, 2026-09-29). A status message brings it
          back, which is when it has something to say. */}
      {stripHasContent && (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.25rem",
          padding: "0.3rem 0.5rem",
          borderBottom: "1px solid var(--qf-border)",
          background: "var(--qf-surface-alt)",
          flex: "0 0 auto",
        }}
      >
        {/* A lone tab is chrome that says nothing: with the rollups hidden
            (see `dataFramesOf`) only Symbols is left, and a button that is
            always selected with no alternative to switch to is a second label
            for the panel the user is already looking at. The strip itself
            stays — it carries Refresh, the status line and Columns. */}
        {dataFrames.length > 1 &&
          dataFrames.map((frame) => {
            const selected = frame.name === current?.name;
            return (
              <button
                key={frame.name}
                onClick={() => setActive(frame.name)}
                style={{
                  border: "none",
                  borderRadius: 4,
                  padding: "0.25rem 0.6rem",
                  fontSize: "0.78rem",
                  cursor: "pointer",
                  background: selected ? "var(--qf-selection)" : "transparent",
                  color: selected ? "var(--qf-text)" : "var(--qf-text-secondary)",
                }}
              >
                {TAB_LABELS[frame.name] ?? frame.name}
                <span style={{ opacity: 0.6, marginLeft: "0.35rem" }}>{frame.rows.length}</span>
              </button>
            );
          })}
        {/* One right-aligned group, so the auto margin belongs to it rather than
            to whichever of these happens to render first — Refresh and Columns
            are both conditional, and hanging it on a conditional child put the
            status line back against the tabs whenever that child was absent. */}
        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          {/* Ahead of the row count, because it is what *changes* the count. */}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={refreshing}
              title="Recalculate this screen from the latest data"
              style={{
                border: "1px solid var(--qf-border)",
                borderRadius: 4,
                padding: "0.15rem 0.45rem",
                background: "transparent",
                color: "var(--qf-text-secondary)",
                // Not `not-allowed`: a refresh in flight is the button working,
                // not the button refusing.
                cursor: refreshing ? "progress" : "pointer",
                opacity: refreshing ? 0.6 : 1,
                fontSize: "0.72rem",
              }}
            >
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          )}

          <span style={{ fontSize: "0.72rem", color: "var(--qf-text-muted)" }}>{status}</span>

          {/* Community has no column tool panel, so visibility needs our own UI.
              Offered only on the detail tab: a rollup's columns are derived. */}
          {isDetail && onConfigChange && !hideColumns && (
            <button
              onClick={() => setPickerOpen((open) => !open)}
              title="Choose columns"
              style={{
                border: "1px solid var(--qf-border)",
                borderRadius: 4,
                padding: "0.15rem 0.45rem",
                background: pickerOpen ? "var(--qf-selection)" : "transparent",
                color: "var(--qf-text-secondary)",
                cursor: "pointer",
                fontSize: "0.72rem",
              }}
            >
              Columns
            </button>
          )}
        </div>
      </div>
      )}

      {/* The filter row that used to sit here is gone: the toolbar's saved-filter
          picker does the same job with more room, and a second filter surface
          directly above the grid meant two places to look and two to keep in
          step. What is left of it — how many rows the filter kept — moved into
          the status line, where the row count already was. */}

      {pickerOpen && isDetail && onConfigChange && !hideColumns && (
        <ColumnPicker
          universe={universe}
          columns={currentConfig.columns ?? []}
          onChange={(columns) => onConfigChange({ columns })}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {builderTab && isDetail && onConfigChange && (
        <ScreenerBuilder
          config={config}
          fields={current?.fields ?? []}
          meta={meta}
          initialTab={builderTab}
          onConfigChange={onConfigChange}
          // Saving a filter needs a contract-valid ULID, which only the host can
          // mint. Without one the library is read-only rather than writing an id
          // the contract would reject.
          onSavedFiltersChange={mintId ? onSavedFiltersChange : undefined}
          canEditLibrary={canEditLibrary}
          mintId={mintId ?? (() => "")}
          onClose={() => setBuilderTab(null)}
        />
      )}

      <div style={autoHeight ? { flex: "0 0 auto" } : { flex: 1, minHeight: 0 }}>
        <ScreenerGrid
          // Remount per frame: each has its own schema and identity column, so
          // reusing one grid instance would carry stale column state across.
          key={current?.name ?? "empty"}
          frame={current}
          config={currentConfig}
          meta={meta}
          sources={sources}
          resolveToken={resolveToken}
          locale={locale}
          // Undefined keeps ScreenerGrid's own default (Quartz dark).
          {...(themeClass ? { themeClass } : {})}
          // Only the detail grid writes back. A rollup's columns and sort are
          // derived, and persisting them would overwrite the detail view's.
          onConfigChange={isDetail ? onConfigChange : undefined}
          // Only the detail tab: a rollup's counts are groups, not rows, and
          // reporting them would make the status line mean two different things.
          onRowCounts={isDetail ? onRowCounts : undefined}
          onExporter={isDetail ? onExporter : undefined}
          // Detail only: a rollup row is a sector, not something to chart.
          onSelectSymbol={isDetail ? onSelectSymbol : undefined}
          onHoverSymbol={isDetail ? onHoverSymbol : undefined}
          onSelectedSymbols={isDetail ? onSelectedSymbols : undefined}
          // Detail only, for the same reason: a sector cannot go on a list.
          track={isDetail ? track : undefined}
          onNewsOpen={isDetail ? onNewsOpen : undefined}
          addRow={isDetail ? addRow : undefined}
          searchScope={searchScope}
          liveWindow={liveWindow}
          categoryOf={categoryOf}
          autoHeight={autoHeight}
        />
      </div>
    </div>
  );
}
